/**
 * Privacy mode, for sharing your screen: every dollar amount and every person's name is blurred
 * until you hover over it (or tap it on a phone, which shows it for a few seconds).
 *
 * It uses the browser's highlight API, which styles stretches of text without changing the page
 * itself, so it can't break anything Svelte draws. Money inputs and charts are blurred with CSS
 * (see app.css, [data-private]). Remembered per device.
 */
const KEY = "privacy";
const MONEY = /[-−]?\$\s?\d[\d,]*(?:\.\d+)?|\b\d{1,3}(?:,\d{3})*\.\d{2}\b/g;

function readOn(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

class Privacy {
  on = $state(readOn());
  private names: string[] = [];
  private observer: MutationObserver | null = null;
  private timer = 0;
  /** Elements you're hovering or just tapped: their text is shown. */
  private revealed = new Set<Element>();
  private ready = false;

  get supported(): boolean {
    return typeof CSS !== "undefined" && "highlights" in CSS && typeof Highlight !== "undefined";
  }

  set(on: boolean) {
    this.on = on;
    try {
      if (on) localStorage.setItem(KEY, "1");
      else localStorage.removeItem(KEY);
    } catch {
      /* fine */
    }
    this.apply();
  }

  /** People's names to hide (only longer than 1 letter, longest first so "Emily Chen" wins). */
  setNames(names: string[]) {
    const next = [...new Set(names.map((n) => n.trim()).filter((n) => n.length > 1))].sort(
      (a, b) => b.length - a.length,
    );
    if (next.join("\n") === this.names.join("\n")) return;
    this.names = next;
    if (this.on) this.schedule();
  }

  /** Called once at start-up. */
  start() {
    if (this.ready || typeof document === "undefined") return;
    this.ready = true;
    const show = (e: Event) => {
      if (!this.on) return;
      const el = (e.target as Element | null)?.closest?.(
        "p, li, td, tr, dd, span, strong, a, button, label, div",
      );
      if (!el) return;
      this.revealed.add(el);
      this.schedule(0);
      if (e.type === "click") {
        setTimeout(() => {
          this.revealed.delete(el);
          this.schedule(0);
        }, 4000);
      }
    };
    const hide = (e: Event) => {
      if (!this.on || e.type === "click") return;
      const el = (e.target as Element | null)?.closest?.(
        "p, li, td, tr, dd, span, strong, a, button, label, div",
      );
      if (el && this.revealed.delete(el)) this.schedule(0);
    };
    document.addEventListener(
      "pointerover",
      (e) => (e as PointerEvent).pointerType === "mouse" && show(e),
    );
    document.addEventListener(
      "pointerout",
      (e) => (e as PointerEvent).pointerType === "mouse" && hide(e),
    );
    document.addEventListener("click", show, true);
    this.apply();
  }

  private apply() {
    if (typeof document === "undefined") return;
    if (this.on) document.documentElement.dataset.private = "1";
    else delete document.documentElement.dataset.private;
    if (!this.supported) return;
    if (!this.on) {
      this.observer?.disconnect();
      this.observer = null;
      CSS.highlights.delete("private");
      return;
    }
    if (!this.observer) {
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(document.body, { subtree: true, childList: true, characterData: true });
    }
    this.schedule(0);
  }

  private schedule(ms = 60) {
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.paint(), ms);
  }

  private paint() {
    if (!this.on || !this.supported) return;
    const nameRe = this.names.length
      ? new RegExp(`(?<![\\p{L}])(?:${this.names.map(escape).join("|")})(?![\\p{L}])`, "gu")
      : null;
    const ranges: Range[] = [];
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => {
        const p = n.parentElement;
        if (!p || !n.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
        if (p.closest("script, style, textarea, [data-public]")) return NodeFilter.FILTER_REJECT;
        for (const el of this.revealed) if (el.contains(p)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      const text = n.nodeValue!;
      for (const re of nameRe ? [MONEY, nameRe] : [MONEY]) {
        re.lastIndex = 0;
        for (let m = re.exec(text); m; m = re.exec(text)) {
          if (!m[0]) {
            re.lastIndex++;
            continue;
          }
          const r = new Range();
          r.setStart(n, m.index);
          r.setEnd(n, m.index + m[0].length);
          ranges.push(r);
        }
      }
    }
    CSS.highlights.set("private", new Highlight(...ranges));
  }
}

export const privacy = new Privacy();
